import { useEffect, useState } from "react";
import { Room, Player } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Sparkles, Home, Share2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

interface Props {
  room: Room;
  players: Player[];
  amHost: boolean;
}

export default function PokerSummary({ room, players, amHost }: Props) {
  const navigate = useNavigate();
  const [refinedCount, setRefinedCount] = useState<number | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { count } = await supabase
        .from("rounds")
        .select("*", { count: "exact", head: true })
        .eq("room_id", room.id)
        .eq("refined", true);
      if (active) setRefinedCount(count ?? 0);
      const { data } = await supabase
        .from("rooms")
        .select("created_at")
        .eq("id", room.id)
        .maybeSingle();
      if (active) setCreatedAt((data as any)?.created_at ?? null);
    })();
    return () => {
      active = false;
    };
  }, [room.id]);

  const durationMin = createdAt
    ? Math.max(1, Math.round((Date.now() - new Date(createdAt).getTime()) / 60000))
    : null;

  const share = () => {
    const text = `🃏 Scrum Poker session\nRoom ${room.code}\nStories refined: ${refinedCount ?? 0}${
      durationMin ? `\nDuration: ${durationMin} min` : ""
    }`;
    navigator.clipboard.writeText(text);
    toast.success("Summary copied!");
  };

  const newSession = async () => {
    await supabase.from("rooms").update({ status: "lobby", current_round_id: null }).eq("id", room.id);
  };

  return (
    <main className="min-h-screen p-4 py-8 flex items-center">
      <div className="max-w-md mx-auto w-full space-y-6 animate-pop-in">
        <div className="text-center">
          <Sparkles className="h-14 w-14 mx-auto text-primary mb-2" />
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Session complete</div>
          <h1 className="text-3xl font-extrabold">Nice work, team!</h1>
        </div>

        <Card className="p-6 border-2 shadow-pop space-y-4">
          <div className="text-center">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Stories refined
            </div>
            <div className="text-6xl font-extrabold gradient-primary bg-clip-text text-transparent">
              {refinedCount ?? "—"}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            <div className="text-center">
              <div className="text-xs font-semibold text-muted-foreground">Participants</div>
              <div className="text-2xl font-extrabold">{players.length}</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-semibold text-muted-foreground">Duration</div>
              <div className="text-2xl font-extrabold">{durationMin ? `${durationMin}m` : "—"}</div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={share} variant="outline" className="gap-2">
            <Share2 className="h-4 w-4" /> Share
          </Button>
          {amHost ? (
            <Button onClick={newSession} className="gap-2 gradient-primary font-bold">
              <RotateCcw className="h-4 w-4" /> New session
            </Button>
          ) : (
            <Button onClick={() => navigate("/")} className="gap-2 gradient-primary font-bold">
              <Home className="h-4 w-4" /> Home
            </Button>
          )}
        </div>
        <Button onClick={() => navigate("/")} variant="ghost" className="w-full gap-2">
          <Home className="h-4 w-4" /> Home
        </Button>
      </div>
    </main>
  );
}
